import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Map } from 'immutable'

import ContextSubscriber from '@aytm/lib/common/components/decorators/ContextSubscriber'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'

import { isSkipperGuidedDraftReport } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'

import { addListItem, revertListItem, updateListItem } from '@aytm/app/stats/page/sidebar/reports/actions'

// components
import { Button } from 'primereact/button'
import { SplitButton } from 'primereact/splitbutton'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'

// <Component>
export function ReportControlsWithContext() {
  return ContextSubscriber(
    {
      name: 'report',
      contextTypes: {
        state: PropTypes.instanceOf(Map).isRequired,
        model: PropTypes.instanceOf(Map).isRequired
      },
      mapToProps(values) {
        return {
          state: values.state,
          model: values.model
        }
      }
    },
    {
      name: 'reportList',
      contextTypes: {
        url: PropTypes.string.isRequired,
        state: PropTypes.instanceOf(Map).isRequired,
        model: PropTypes.instanceOf(Map).isRequired,
        limits: PropTypes.instanceOf(Map).isRequired,
        actions: PropTypes.object.isRequired,
        virtualReportsIds: PropTypes.array.isRequired,
        disabled: PropTypes.bool,
        isSurveyGuest: PropTypes.bool,
        isSurveyViewer: PropTypes.bool
      },
      mapToProps(values) {
        return {
          url: values.url,
          listState: values.state,
          listModel: values.model,
          limits: values.limits,
          actions: values.actions,
          virtualReportsIds: values.virtualReportsIds,
          disabled: values.disabled,
          isSurveyGuest: values.isSurveyGuest,
          isSurveyViewer: values.isSurveyViewer
        }
      }
    }
  )(ReportControls)
}

export class ReportControls extends ManagedRenderComponent {
  static propTypes = {
    url: PropTypes.string.isRequired,
    state: PropTypes.instanceOf(Map).isRequired,
    model: PropTypes.instanceOf(Map).isRequired,
    listState: PropTypes.instanceOf(Map).isRequired,
    listModel: PropTypes.instanceOf(Map).isRequired,
    limits: PropTypes.instanceOf(Map).isRequired,
    actions: PropTypes.object.isRequired,
    virtualReportsIds: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    isCompact: PropTypes.bool,
    isSurveyGuest: PropTypes.bool,
    isSurveyViewer: PropTypes.bool
  }
  static updateOnProps(nextProps, prevProps) {
    return (
      nextProps.state != prevProps.state ||
      nextProps.model != prevProps.model ||
      nextProps.limits != prevProps.limits ||
      nextProps.listState != prevProps.listState ||
      nextProps.disabled != prevProps.disabled
    )
  }

  constructor(...args) {
    super(...args)
    this.getModelProps = this.getModelProps.bind(this)
    this.onReportAdd = this.onReportAdd.bind(this)
    this.onReportRevert = this.onReportRevert.bind(this)
    this.onReportUpdate = this.onReportUpdate.bind(this)
  }

  getModelProps() {
    const { url, state, model, listState, listModel, actions } = this.props

    return {
      url,
      listState,
      listModel,
      state,
      model,
      actions
    }
  }

  onReportAdd() {
    addListItem({ ...this.getModelProps() })
  }

  onReportRevert() {
    revertListItem({ ...this.getModelProps() })
  }

  onReportUpdate() {
    updateListItem({ ...this.getModelProps() })
  }

  render() {
    const {
      state,
      model,
      listState,
      listModel,
      virtualReportsIds,
      limits,
      disabled,
      isCompact,
      isSurveyGuest,
      isSurveyViewer
    } = this.props
    const schema = model.get('schema')
    const isChanged = state.get('changed')
    const isProcessing = state.has('process')
    const isEmpty =
      (null !== schema && !schema.size && !['xpert', 'xpert_custom'].includes(model.get('type'))) ||
      isSkipperGuidedDraftReport(model)
    const isEditable =
      state.get('editable') &&
      !['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(model.get('type'))
    const isPanelDisabled = listState.get('disabled') || disabled
    const atReportsCap = listModel.get('items').size >= limits.getIn(['reports', 'max'])
    const isDisabled = isPanelDisabled || atReportsCap
    const isLocked = ['widget'].includes(model.get('type')) || isProcessing || isSurveyGuest || isSurveyViewer
    const isAdding = isProcessing && 'add' == state.get('process')
    const isSaving = isProcessing && 'save' == state.get('process')

    const options = []
    if (isCompact && !isSurveyGuest && !isSurveyViewer) {
      isChanged &&
        options.push({
          label: isEditable
            ? 'Revert'
            : ['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(model.get('type'))
            ? 'Reset'
            : 'Update',
          command: this.onReportRevert
        })
      isEditable &&
        !isEmpty &&
        !isDisabled && // the report ceiling gates this entry as it gates the primary action
        options.push({
          label: 'Save as...',
          command: this.onReportAdd
        })
    }

    // the action the primary half will run, null when it has none to offer. The ceiling closes
    // adds only, so an update on an existing report stays available at it.
    const primaryAction = isCompact
      ? isEmpty || isPanelDisabled || isLocked
        ? null
        : isEditable
        ? isChanged
          ? this.onReportUpdate
          : null
        : atReportsCap
        ? null
        : this.onReportAdd
      : null

    return isCompact ? (
      0 < options.length ? (
        <SplitButton
          className={classNames('p-button-stat p-button-sm p-button-rounded', {
            // presentation only — a disabled prop would reach the dropdown, and at the
            // ceiling Revert is the one action left in it
            'p-button-disabled': !primaryAction
          })}
          model={options}
          label={isAdding || isSaving ? 'Saving...' : isEditable ? (isChanged ? 'Save' : 'Saved') : 'Save as'}
          dropdownIcon={isAdding || isSaving ? 'pi pi-spin pi-spinner' : <i className="icon-aytmfd-arrow-v2" />}
          appendTo="self"
          onClick={primaryAction}
        />
      ) : isSurveyGuest || isSurveyViewer ? (
        <Button
          className="p-button-stat p-button-sm p-button-rounded"
          label={
            isChanged
              ? isEditable
                ? 'Revert'
                : ['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(
                    model.get('type')
                  )
                ? 'Reset'
                : 'Update'
              : 'Saved'
          }
          loading={isProcessing && 'revert' == state.get('process')}
          disabled={disabled || isProcessing || !isChanged}
          onClick={disabled || isProcessing || !isChanged ? null : this.onReportRevert}
        />
      ) : (
        <Button
          className="p-button-stat p-button-sm p-button-rounded"
          label={isAdding ? 'Saving...' : 'Save as'}
          iconPos="right"
          loading={isAdding}
          disabled={isEmpty || isDisabled || isLocked}
          onClick={isEmpty || isDisabled || isLocked ? null : this.onReportAdd}
        />
      )
    ) : isChanged ? (
      <>
        <div className={`${style['content-row-column']} ${style['content-row-column-buttons']}`}>
          <div className={style['content-row-column']}>
            <Button
              className="p-button-stat"
              label={
                isEditable
                  ? 'REVERT'
                  : ['original', 'xpert', 'widget', 'skipper', 'concept', ...virtualReportsIds].includes(
                      model.get('type')
                    )
                  ? 'RESET'
                  : 'UPDATE'
              }
              loading={isProcessing && 'revert' == state.get('process')}
              disabled={disabled || isProcessing}
              onClick={disabled || isProcessing ? null : this.onReportRevert}
            />
          </div>
          <div className={style['content-row-column']}>
            {isEditable ? (
              <Button
                className="p-button-stat"
                label={isAdding || isSaving ? 'SAVING' : 'SAVE FOR EVERYONE'}
                loading={isAdding || isSaving}
                disabled={disabled || isEmpty || isLocked}
                onClick={disabled || isEmpty || isLocked ? null : this.onReportUpdate}
              />
            ) : (
              <Button
                className="p-button-stat"
                label={isAdding || isSaving ? 'SAVING' : 'SAVE AS A NEW REPORT'}
                loading={isAdding || isSaving}
                disabled={isEmpty || isDisabled || isLocked}
                onClick={isEmpty || isDisabled || isLocked ? null : this.onReportAdd}
              />
            )}
          </div>
        </div>
      </>
    ) : null
  }
}

export default ReportControlsWithContext()
